import { logger } from '../../../loader/essentials/logger.js';
import { deleteSourcepointCustomConsent, getSourcepointConsents } from './utiq-sync-sourcepoint-helper.js';

const log = logger.logGroup({ prefix: 'Utiq (Sourcepoint-Sync)' });

declare const window: Window & utiq.Window & sourcepoint.SourcePointWindow;

export interface UtiqSyncSourcepointParams {
    customVendorId: string;
    customPurposeId: string;
}

export const setupUtiqSyncSourcepoint = ({ customVendorId, customPurposeId }: UtiqSyncSourcepointParams) => {

    /* initialize required global variables */
    window.Utiq ||= {};
    window.Utiq.API ||= {};
    window.Utiq.queue ||= [];
    window._sp_ ||= {};
    window._sp_queue ||= [];

    const getCmpConsentStatus = async () => new Promise<boolean | undefined>((resolve) => {
        window.__tcfapi('getCustomVendorConsents', 2, async (data: { newUser: boolean }) => {
            if (data.newUser) {
                resolve(undefined);
                return;
            }
            try {
                const _sp_ = window._sp_ as sourcepoint.SourcePointLoaded;
                const sourcepointConsentsData = await getSourcepointConsents({
                    baseEndpoint: _sp_.config?.baseEndpoint,
                    propertyId: _sp_.metricData.propertyId,
                });
                const customVendor = sourcepointConsentsData.grants[customVendorId];
                const customVendorConsent: boolean | undefined = customVendor?.vendorGrant;
                const customPurposeConsent: boolean | undefined = customVendor?.purposeGrants[customPurposeId];
                if (customVendorConsent === undefined) throw new Error(`Custom vendor with id ${customVendorId} not found`);
                if (customPurposeConsent === undefined) throw new Error(`Custom purpose with id ${customPurposeId} not found for vendor ${customVendorId}`);
                resolve(customPurposeConsent === true && customVendorConsent !== false);
            } catch (err: unknown) {
                log.error(`Error retrieving consent data from SourcePoint: ${err instanceof Error ? err.message : String(err)}`);
                resolve(undefined);
            }
        });
    });

    const setCmpConsentStatus = async (consent: boolean): Promise<void> => {
        log.debug(`send consent=${consent} to SourcePoint`);
        if (consent) {
            return new Promise<void>((resolve) => {
                // @ts-ignore TODO how to allow more params?
                window.__tcfapi('postCustomConsent', 2, resolve, [customVendorId], [customPurposeId], []);
            });
        }
        try {
            const _sp_ = window._sp_ as sourcepoint.SourcePointLoaded;
            await deleteSourcepointCustomConsent({
                baseEndpoint: _sp_.config?.baseEndpoint,
                propertyId: _sp_.metricData.propertyId,
                vendors: [customVendorId],
                categories: [customPurposeId],
                legIntCategories: [],
            });
        } catch (err: unknown) {
            log.error(`Error deleting consent data from SourcePoint: ${err instanceof Error ? err.message : String(err)}`);
        }
    }

    const getUtiqConsentStatus = async (): Promise<boolean | undefined> => {
        try {
            return window.Utiq.API!.getUtiqConsentStatus!();
        } catch (err: unknown) {
            log.error(`Utiq.API.getUtiqConsentStatus() call failed. Reason: ${err instanceof Error ? err.message : String(err)}`);
            return undefined;
        }
    }

    const setUtiqConsentStatus = async (consent: boolean | undefined, options?: { syncConsentOnAccept: boolean }) => {
        log.debug(`send consent=${consent} to Utiq`);
        if (consent === undefined) return;
        window.Utiq.API!.handleConsentChange!(consent, options);
    }

    window._sp_queue.push(_sp_ => {
        window.Utiq.queue!.push(async () => {
            // second layer ("privacy manager") button has been clicked
            _sp_.addEventListener('onPrivacyManagerAction', () =>
                _sp_.addEventListener('onConsentReady', async () => {
                    const cmpConsentStatus = await getCmpConsentStatus();
                    log.debug(`sourcepoint event onPrivacyManagerAction and onConsentReady received: cmpConsentStatus=${cmpConsentStatus}`);
                    setUtiqConsentStatus(cmpConsentStatus);
                }),
            );
            // first layer call-to-action button has been clicked (e.g. "accept all")
            _sp_.addEventListener('onMessageChoiceSelect', () =>
                _sp_.addEventListener('onConsentReady', async () => {
                    const cmpConsentStatus = await getCmpConsentStatus();
                    log.debug(`sourcepoint event onMessageChoiceSelect and onConsentReady received: cmpConsentStatus=${cmpConsentStatus}`);
                    setUtiqConsentStatus(cmpConsentStatus, {
                        // syncConsentOnAccept flag should be only set to true when the consent is explicitly accepted
                        syncConsentOnAccept: true,
                    });
                }),
            );

            const update = async (message: string, utiqConsentStatus: boolean | undefined) => {
                const cmpConsentStatus = await getCmpConsentStatus();
                log.debug(`${message}: cmpConsentStatus=${cmpConsentStatus}, utiqConsentStatus=${utiqConsentStatus}`);
                if (utiqConsentStatus === false && cmpConsentStatus) {
                    setCmpConsentStatus(false);
                } else if (utiqConsentStatus === true && cmpConsentStatus === false) {
                    setCmpConsentStatus(true);
                } else if (utiqConsentStatus === undefined && cmpConsentStatus !== undefined) {
                    setUtiqConsentStatus(cmpConsentStatus);
                }
            }

            // TODO: eventually initial sync only if cmp consent was restored?
            const utiqConsentStatus = await getUtiqConsentStatus();
            update('initial sync', utiqConsentStatus);

            window.Utiq.API!.addEventListener!('onConsentUpdateFinished', ({ isConsentGranted: utiqConsentStatus }) => {
                update('utiq event onConsentUpdateFinished received', utiqConsentStatus);
            });

            // TODO: "reject all" triggers a page reload, eventually before utiq consent update is finished
        });
    });

};
